import type { AxiosInstance, AxiosResponse } from "axios";
import { notify } from "@lib/util/logger";
import { appVersion } from "@constants/Env";

type ApiResponse<T = object> = {
  errors?: Array<{
    message: string;
    locations: Array<{ [index: string]: unknown }>;
    path: unknown[];
    extensions: {
      code: string;
    };
  }>;
  data: T;
};

interface WebUploadConfig {
  apiEndpoint: string;
  /** GraphQL operations JSON（query + variables） */
  operationsJson: string;
  /** GraphQL multipart request spec のファイルマッピング JSON */
  mapJson: string;
  /** アップロードするファイル（キー: FormDataのフィールド名, 値: File） */
  files: Record<string, File>;
  /** Authorization等のヘッダー */
  headers: Record<string, string>;
  /** Axiosインスタンス（Strategy 1, 2で使用） */
  axiosInstance: AxiosInstance;
  /** X-Request-ID（ログ紐付け用） */
  requestId: string;
}

/**
 * Web環境でのFormDataアップロード。5つの戦略を順次試行するフォールバック方式。
 *
 * 参照:
 * - axios#6454 (FormData stream consumed)
 * - axios#6898 (iOS 18.3.2 Network Error)
 * - axios#6240 (FormData retry headers)
 * - WebKit Bug#165897 (Safari 60s XHR timeout)
 */
export async function tryWebFormDataUploadFallback(
  config: WebUploadConfig
): Promise<AxiosResponse<ApiResponse>> {
  const strategies: Array<{
    name: string;
    execute: () => Promise<AxiosResponse<ApiResponse>>;
  }> = [
    {
      name: "Strategy 1: Rebuild FormData + Axios transformRequest",
      execute: () => webStrategyAxiosRebuild(config),
    },
    {
      name: "Strategy 2: Native fetch API",
      execute: () => webStrategyFetch(config),
    },
    {
      name: "Strategy 3: XMLHttpRequest with progress monitoring",
      execute: () => webStrategyXHR(config),
    },
    {
      name: "Strategy 4: fetch minimal headers",
      execute: () => webStrategyFetchMinimal(config),
    },
    {
      name: "Strategy 5: XMLHttpRequest with stall detection",
      execute: () => webStrategyXHRWithStallDetection(config),
    },
  ];

  let lastError: Error | null = null;

  /* eslint-disable no-await-in-loop, no-continue */
  for (let i = 0; i < strategies.length; i += 1) {
    const strategy = strategies[i];

    // オフラインなら復帰を待つ
    if (!navigator.onLine) {
      await waitForOnline();
    }

    // 2番目以降はbackoff
    if (i > 0) {
      const delay = calculateBackoffWithJitter(i - 1);
      await sleep(delay);
    }

    try {
      await notify(
        {
          type: "Web FormData Upload Strategy Attempt",
          searchableType: "WEB_FORMDATA_STRATEGY_ATTEMPT",
          details: {
            strategyIndex: i + 1,
            strategyName: strategy.name,
            fileCount: Object.keys(config.files).length,
            requestId: config.requestId,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "debug", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      const response = await strategy.execute();

      await notify(
        {
          type: "Web FormData Upload Strategy Success",
          searchableType: "WEB_FORMDATA_STRATEGY_SUCCESS",
          details: {
            strategyIndex: i + 1,
            strategyName: strategy.name,
            requestId: config.requestId,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "info", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // 4xxは即失敗
      if (!isRetryableError(error)) {
        await notify(
          {
            type: "Web FormData Upload Non-Retryable Error",
            searchableType: "WEB_FORMDATA_NON_RETRYABLE",
            details: {
              strategyIndex: i + 1,
              strategyName: strategy.name,
              errorMessage: lastError.message,
              requestId: config.requestId,
              timestamp: new Date().toISOString(),
              version: appVersion,
            },
          },
          { level: "error", skipDeviceInfo: true }
        ).catch(() => {
          // 通知エラーは無視
        });
        throw error;
      }

      await notify(
        {
          type: "Web FormData Upload Strategy Failed",
          searchableType: "WEB_FORMDATA_STRATEGY_FAILED",
          details: {
            strategyIndex: i + 1,
            strategyName: strategy.name,
            errorMessage: lastError.message,
            willRetry: i < strategies.length - 1,
            requestId: config.requestId,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "warning", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      if (i < strategies.length - 1) {
        continue;
      }
    }
  }
  /* eslint-enable no-await-in-loop, no-continue */

  await notify(
    {
      type: "Web FormData Upload All Strategies Failed",
      searchableType: "WEB_FORMDATA_ALL_STRATEGIES_FAILED",
      details: {
        totalStrategies: strategies.length,
        lastError: lastError?.message ?? "Unknown error",
        requestId: config.requestId,
        timestamp: new Date().toISOString(),
        version: appVersion,
      },
    },
    { level: "error", skipDeviceInfo: true }
  ).catch(() => {
    // 通知エラーは無視
  });

  throw lastError ?? new Error("All web upload strategies failed");
}

// ============================================================
// FormData再構築ユーティリティ
// ============================================================

/** FormDataを再構築する（ストリームは1回で消費されるためリトライ時に必要） */
function buildFreshFormData(config: WebUploadConfig): FormData {
  const fd = new FormData();
  fd.append("operations", config.operationsJson);
  fd.append("map", config.mapJson);

  Object.entries(config.files).forEach(([key, file]) => {
    fd.append(key, file, file.name);
  });

  return fd;
}

// ============================================================
// 各戦略の実装
// ============================================================

/** Strategy 1: FormData再構築 + Axios（transformRequestでContent-Type削除） */
async function webStrategyAxiosRebuild(
  config: WebUploadConfig
): Promise<AxiosResponse<ApiResponse>> {
  const freshFormData = buildFreshFormData(config);

  const { data }: { data: AxiosResponse<ApiResponse> } =
    await config.axiosInstance.post(config.apiEndpoint, freshFormData, {
      _isMultipartUpload: true,
      headers: {
        "X-Request-ID": config.requestId,
      },
      timeout: 1800000,
      transformRequest: [
        (requestData, requestHeaders) => {
          // ブラウザにboundary付きContent-Typeを自動設定させる
          if (requestData instanceof FormData && requestHeaders != null) {
            // eslint-disable-next-line no-param-reassign
            delete requestHeaders["Content-Type"];
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return requestData;
        },
      ],
    });

  return data;
}

/** Strategy 2: Native fetch API（Axiosバイパス、iOS 18.3.2バグ回避） */
async function webStrategyFetch(
  config: WebUploadConfig
): Promise<AxiosResponse<ApiResponse>> {
  const freshFormData = buildFreshFormData(config);

  const response = await fetch(config.apiEndpoint, {
    method: "POST",
    body: freshFormData,
    headers: {
      // Content-Typeはブラウザに任せる
      Authorization: config.headers.Authorization ?? "",
      "X-Requested-With": "XMLHttpRequest",
      AppVersion: appVersion ?? "unknown",
      "X-Request-ID": config.requestId,
    },
  });

  if (!response.ok) {
    throw new Error(
      `Fetch API failed: ${response.status} ${response.statusText}`
    );
  }

  const responseData = (await response.json()) as ApiResponse;

  const axiosHeaders: Record<string, string> = {};
  response.headers.forEach((value: string, key: string) => {
    axiosHeaders[key] = value;
  });

  return {
    data: responseData,
    status: response.status,
    statusText: response.statusText,
    headers: axiosHeaders,
    config: {},
  } as unknown as AxiosResponse<ApiResponse>;
}

/** Strategy 3: XMLHttpRequest（進捗監視付き） */
async function webStrategyXHR(
  config: WebUploadConfig
): Promise<AxiosResponse<ApiResponse>> {
  const freshFormData = buildFreshFormData(config);

  return new Promise<AxiosResponse<ApiResponse>>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.onload = () => {
      try {
        if (xhr.status >= 200 && xhr.status < 300) {
          const responseData = JSON.parse(xhr.responseText) as ApiResponse;

          const responseHeaders: Record<string, string> = {};
          const headersStr = xhr.getAllResponseHeaders();
          headersStr.split("\r\n").forEach((line) => {
            const [key, ...rest] = line.split(": ");
            if (key != null && rest.length > 0) {
              responseHeaders[key] = rest.join(": ");
            }
          });

          resolve({
            data: responseData,
            status: xhr.status,
            statusText: xhr.statusText,
            headers: responseHeaders,
            config: {},
          } as unknown as AxiosResponse<ApiResponse>);
        } else {
          reject(new Error(`XHR failed: ${xhr.status} ${xhr.statusText}`));
        }
      } catch (error) {
        reject(
          error instanceof Error ? error : new Error("XHR response parse error")
        );
      }
    };

    xhr.onerror = () => {
      reject(new Error("XHR Network Error"));
    };

    xhr.ontimeout = () => {
      reject(new Error("XHR Timeout"));
    };

    xhr.open("POST", config.apiEndpoint);
    xhr.timeout = 1800000;

    // Content-Typeはブラウザに任せる
    Object.entries(config.headers).forEach(([key, value]) => {
      if (key !== "Content-Type") {
        xhr.setRequestHeader(key, value);
      }
    });
    xhr.setRequestHeader("X-Request-ID", config.requestId);
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

    xhr.send(freshFormData);
  });
}

/** Strategy 4: fetch + ミニマルヘッダー（Authorizationのみ） */
async function webStrategyFetchMinimal(
  config: WebUploadConfig
): Promise<AxiosResponse<ApiResponse>> {
  const freshFormData = buildFreshFormData(config);

  const response = await fetch(config.apiEndpoint, {
    method: "POST",
    body: freshFormData,
    headers: {
      Authorization: config.headers.Authorization ?? "",
      AppVersion: config.headers.AppVersion ?? appVersion ?? "unknown",
    },
  });

  if (!response.ok) {
    throw new Error(
      `Fetch minimal failed: ${response.status} ${response.statusText}`
    );
  }

  const responseData = (await response.json()) as ApiResponse;

  return {
    data: responseData,
    status: response.status,
    statusText: response.statusText,
    headers: {},
    config: {},
  } as unknown as AxiosResponse<ApiResponse>;
}

/** Strategy 5: XMLHttpRequest + スタール検出（45秒間進捗なしでabort） */
async function webStrategyXHRWithStallDetection(
  config: WebUploadConfig
): Promise<AxiosResponse<ApiResponse>> {
  const freshFormData = buildFreshFormData(config);
  const STALL_TIMEOUT_MS = 45000;

  return new Promise<AxiosResponse<ApiResponse>>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    const resetStallTimer = (): void => {
      if (stallTimer != null) {
        clearTimeout(stallTimer);
      }
      stallTimer = setTimeout(() => {
        xhr.abort();
        reject(new Error("XHR upload stalled (no progress for 45s)"));
      }, STALL_TIMEOUT_MS);
    };

    // 進捗があればタイマーリセット
    xhr.upload.onprogress = () => {
      resetStallTimer();
    };

    xhr.onload = () => {
      if (stallTimer != null) clearTimeout(stallTimer);
      try {
        if (xhr.status >= 200 && xhr.status < 300) {
          const responseData = JSON.parse(xhr.responseText) as ApiResponse;
          resolve({
            data: responseData,
            status: xhr.status,
            statusText: xhr.statusText,
            headers: {},
            config: {},
          } as unknown as AxiosResponse<ApiResponse>);
        } else {
          reject(
            new Error(
              `XHR stall-detect failed: ${xhr.status} ${xhr.statusText}`
            )
          );
        }
      } catch (error) {
        reject(
          error instanceof Error
            ? error
            : new Error("XHR stall-detect response parse error")
        );
      }
    };

    xhr.onerror = () => {
      if (stallTimer != null) clearTimeout(stallTimer);
      reject(new Error("XHR stall-detect Network Error"));
    };

    xhr.ontimeout = () => {
      if (stallTimer != null) clearTimeout(stallTimer);
      reject(new Error("XHR stall-detect Timeout"));
    };

    xhr.open("POST", config.apiEndpoint);
    xhr.timeout = 1800000;

    Object.entries(config.headers).forEach(([key, value]) => {
      if (key !== "Content-Type") {
        xhr.setRequestHeader(key, value);
      }
    });
    xhr.setRequestHeader("X-Request-ID", config.requestId);
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

    resetStallTimer(); // 初回タイマー開始
    xhr.send(freshFormData);
  });
}

// ============================================================
// ユーティリティ
// ============================================================

/** Exponential backoff with full jitter */
function calculateBackoffWithJitter(attempt: number): number {
  const BASE_DELAY_MS = 1000;
  const MAX_DELAY_MS = 30000;
  const exponential = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);
  return Math.floor(Math.random() * exponential);
}

/** リトライ可能なエラーか判定（Network Error, timeout, 408, 429, 5xx） */
function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError) return true; // fetch network error
  if (error instanceof Error) {
    const msg = error.message;
    // ブラウザごとにメッセージが異なるため文字列マッチ
    if (
      msg === "Network Error" ||
      msg.includes("Failed to fetch") ||
      msg.includes("NetworkError") ||
      msg.includes("connection") ||
      msg.includes("offline") ||
      msg.includes("XHR Network Error") ||
      msg.includes("stalled") ||
      msg.includes("Timeout") ||
      msg.includes("timeout")
    ) {
      return true;
    }

    // ステータスコードで判定
    const statusMatch = msg.match(/(\d{3})/);
    if (statusMatch != null) {
      const status = parseInt(statusMatch[1], 10);
      if (status === 408 || status === 429 || status >= 500) return true;
      // 4xxはリトライ不可
      if (status >= 400 && status < 500) return false;
    }
  }
  return true; // 不明なエラーはリトライ可能として扱う
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** オンライン復帰まで待機（最大60秒） */
function waitForOnline(): Promise<void> {
  if (navigator.onLine) return Promise.resolve();

  return new Promise((resolve) => {
    const handler = (): void => {
      window.removeEventListener("online", handler);
      resolve();
    };
    window.addEventListener("online", handler);

    // 最大60秒で諦める
    setTimeout(() => {
      window.removeEventListener("online", handler);
      resolve();
    }, 60000);
  });
}
